import { pick, prop } from 'ramda';
import React, { memo } from 'react';
import { useTranslations } from '@core/js/hooks/i18n';
import { getCleanPath } from '@theme/js/utils/url.util';
import { Link } from '@theme/js/components/common/Link';
import { useAppSelector, useDeviceSelector, useUserSelector } from '@core/js/store/hooks';
import { CATEGORY_VIEWS } from '@core/js/hooks/games/types';

import * as Types from '@core/js/components/main/GamesGrid/components/GamesHeader/types';
import { replaceNameTag } from './helpers';
import { categorySelector } from '@core/js/components/main/GamesGrid/store/helpers';
import { useDynamicReducerSelector } from '@core/js/components/main/GamesGrid/store';

import style from './GamesHeader.scss';

export const GamesHeaderBare: React.FC<Types.IGamesHeaderProps> = ({
    feedName,
    categoryName,
    allGamesCount,
    categoryRowsCount = 0,
    categoryLayoutWithGames = []
}) => {
    const translate = useTranslations();
    const formFactor = useDeviceSelector(prop('formFactor'));
    const { isLoggedIn, userDetails: { firstName } } = useUserSelector(pick(['isLoggedIn', 'userDetails']));
    const category = useDynamicReducerSelector(categorySelector(categoryName));
    const gamesGridViewMorePageURL = getCleanPath(useAppSelector(prop('gamesGridViewMorePageURL')));
    const { categoryConfiguration, seoFriendlyCategoryName = '' } = category || {};
    const {
        categoryIcon,
        categoryView,
        isShownViewMore,
        categoryDescription,
        isShownCategoryName,
        isShownSliderArrows,
        isShownViewMoreGamesNumber,
        pggBecauseYouPlayedGameName = '',
        categoryGameGridLayout
    } = categoryConfiguration || {};
    const isShowMoreShown =
        isShownViewMore &&
        gamesGridViewMorePageURL &&
        Boolean(categoryRowsCount) &&
        categoryLayoutWithGames.length > 1;

    // Lugano changes
    const isSliderControlsVisible = (categoryView === CATEGORY_VIEWS.slider || formFactor !== 'desktop' && categoryView === CATEGORY_VIEWS.carousel)
        && formFactor !== 'mobile' && categoryRowsCount > 1 && isShownSliderArrows;

    const isCategoryTitleShown =
        categoryIcon ||
        isShownCategoryName ||
        isSliderControlsVisible ||
        categoryView !== CATEGORY_VIEWS.webcontent && isShowMoreShown;

    // Lugano changes
    const showControlRightMargin = formFactor === 'desktop'
        ? categoryGameGridLayout?.rows !== '1' && isSliderControlsVisible
        : isSliderControlsVisible;

    const headingRow = translate(`playtech.mobile.system.games-grid.category.${feedName}.${category?.name}`, [pggBecauseYouPlayedGameName], category?.name || '');
    const heading = replaceNameTag(headingRow, isLoggedIn, firstName);
    /* Lugano changes */

    return (
        <>
            {isCategoryTitleShown && (
                <div className={`${style.categoryTitle} cms-games-grid-category-title`}>
                    {categoryIcon && (
                        <img
                            className={style.categoryTitleImage}
                            alt={translate('playtech.mobile.system.games-grid.category.icon.alt')}
                            src={categoryIcon}
                        />
                    )}

                    {isShownCategoryName && Boolean(category?.name) && (
                        <h3 className={style.categoryName}>
                            {heading}
                        </h3>
                    )}

                    {isShowMoreShown && (
                        <Link
                            className={`${style.viewMore} ${showControlRightMargin ? style.viewMoreMargin : ''}`} // Lugano changes
                            href={`${gamesGridViewMorePageURL}/${feedName}/${seoFriendlyCategoryName}${location.search}${location.hash}`}
                        >
                            {translate('playtech.mobile.system.games-grid.category.viewMore')}
                            {isShownViewMoreGamesNumber && Boolean(allGamesCount) && ` (${allGamesCount})`}
                        </Link>
                    )}
                </div>
            )}

            {categoryDescription && (
                <div className={`${style.categoryDescription} cms-games-grid-category-description`}>
                    {translate(categoryDescription, categoryDescription)}
                </div>
            )}
        </>
    );
};

export const GamesHeader = memo(GamesHeaderBare);
